import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Loader2, Plus, Edit, Trash2, Clock, DollarSign, Percent } from 'lucide-react';
import PageHelp from '../components/help/PageHelp';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ServicesPage() {
  const { token, user } = useAuth();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  
  // Form state
  const [name, setName] = useState('');
  const [pricingModel, setPricingModel] = useState('PER_SERVICE');
  const [durationMinutes, setDurationMinutes] = useState('');
  const [basePriceCents, setBasePriceCents] = useState('');
  const [description, setDescription] = useState('');
  const [maxGroupSize, setMaxGroupSize] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // Deposit state
  const [depositEnabled, setDepositEnabled] = useState(false);
  const [depositType, setDepositType] = useState('');
  const [depositFixedAmount, setDepositFixedAmount] = useState('');
  const [depositPercentage, setDepositPercentage] = useState('');

  useEffect(() => {
    loadServices();
  }, []);

  const loadServices = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/providers/${user.id}/services`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setServices(response.data);
    } catch (error) {
      console.error('Error loading services:', error);
      toast.error('Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setName('');
    setPricingModel('PER_SERVICE');
    setDurationMinutes('');
    setBasePriceCents('');
    setDescription('');
    setMaxGroupSize('');
    setEditingService(null);
    setDepositEnabled(false);
    setDepositType('');
    setDepositFixedAmount('');
    setDepositPercentage('');
  };

  const handleOpenDialog = (service = null) => {
    if (service) {
      setEditingService(service);
      setName(service.name);
      setPricingModel(service.pricingModel || 'PER_SERVICE');
      setDurationMinutes(service.durationMinutes ? service.durationMinutes.toString() : '');
      setBasePriceCents(service.basePriceCents ? (service.basePriceCents / 100).toString() : '');
      setDescription(service.description || '');
      setMaxGroupSize(service.maxGroupSize ? service.maxGroupSize.toString() : '');
      setDepositEnabled(service.depositEnabled || false);
      setDepositType(service.depositType || '');
      setDepositFixedAmount(service.depositFixedAmountCents ? (service.depositFixedAmountCents / 100).toString() : '');
      setDepositPercentage(service.depositPercentage?.toString() || '');
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name) {
      toast.error('Service name is required');
      return;
    }

    // Validate deposit settings
    if (depositEnabled) {
      if (!depositType) {
        toast.error('Please select a deposit type');
        return;
      }
      if (depositType === 'FIXED' && !depositFixedAmount) {
        toast.error('Please enter a fixed deposit amount');
        return;
      }
      if (depositType === 'PERCENTAGE' && !depositPercentage) {
        toast.error('Please enter a deposit percentage');
        return;
      }
    }

    try {
      setSubmitting(true);
      
      const serviceData = {
        name,
        pricingModel,
        durationMinutes: durationMinutes ? parseInt(durationMinutes) : null,
        basePriceCents: basePriceCents ? Math.round(parseFloat(basePriceCents) * 100) : null,
        description: description || null,
        maxGroupSize: maxGroupSize ? parseInt(maxGroupSize) : null,
        isActive: true,
        depositEnabled,
        depositType: depositEnabled ? depositType : null,
        depositFixedAmountCents: depositEnabled && depositType === 'FIXED' 
          ? Math.round(parseFloat(depositFixedAmount) * 100) 
          : null,
        depositPercentage: depositEnabled && depositType === 'PERCENTAGE' 
          ? parseInt(depositPercentage) 
          : null
      };

      if (editingService) {
        // Update
        await axios.patch(
          `${API_URL}/api/dashboard/services/${editingService.id}`,
          serviceData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Service updated');
      } else {
        // Create
        serviceData.providerId = user.id;
        await axios.post(
          `${API_URL}/api/dashboard/services`,
          serviceData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Service created');
      }
      
      setDialogOpen(false);
      resetForm();
      loadServices();
    } catch (error) {
      console.error('Error saving service:', error);
      toast.error('Failed to save service');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (serviceId) => {
    if (!window.confirm('Are you sure you want to delete this service?')) {
      return;
    }

    try {
      await axios.delete(
        `${API_URL}/api/dashboard/services/${serviceId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Service deleted');
      loadServices();
    } catch (error) {
      console.error('Error deleting service:', error);
      toast.error('Failed to delete service');
    }
  };

  const formatPrice = (cents, model) => {
    if (!cents) return 'Contact for pricing';
    const price = `$${(cents / 100).toFixed(2)}`;
    const suffix = {
      PER_HORSE: '/horse', PER_HOUR: '/hr', PER_MONTH: '/mo', PER_VISIT: '/visit'
    }[model] || '';
    return price + suffix;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-1">
            <h2 className="text-2xl font-bold">Services</h2>
            <PageHelp
              testId="services"
              title="Services"
              purpose="List what you offer and your prices. Clients pick a service when they book."
              actions={[
                { label: 'Add Service', desc: "create a service with a price and how it's charged" },
                { label: 'Require Deposit', desc: 'ask for a deposit before a job is booked' },
              ]}
              nextStep="Add each service you offer so clients can book the right one."
            />
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => handleOpenDialog()} data-testid="add-service-button">
                <Plus className="mr-2 h-4 w-4" />
                Add Service
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {editingService ? 'Edit Service' : 'Add New Service'}
                </DialogTitle>
                <DialogDescription>
                  {editingService ? 'Update service details' : 'Create a new service offering'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
                <div className="space-y-2">
                  <Label htmlFor="name">Service Name *</Label>
                  <Input
                    id="name"
                    data-testid="service-name-input"
                    placeholder="e.g., Trim, Full Set"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="pricingModel">Pricing Model</Label>
                  <Select value={pricingModel} onValueChange={setPricingModel}>
                    <SelectTrigger data-testid="pricing-model-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PER_SERVICE">Per Service (flat rate)</SelectItem>
                      <SelectItem value="PER_HORSE">Per Horse</SelectItem>
                      <SelectItem value="PER_HOUR">Per Hour</SelectItem>
                      <SelectItem value="PER_MONTH">Per Month</SelectItem>
                      <SelectItem value="PER_VISIT">Per Visit</SelectItem>
                      <SelectItem value="CUSTOM_QUOTE">Custom Quote</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {pricingModel !== 'CUSTOM_QUOTE' && (
                  <div className="space-y-2">
                    <Label htmlFor="price">
                      {pricingModel === 'PER_HORSE' ? 'Price per Horse' :
                       pricingModel === 'PER_HOUR' ? 'Hourly Rate' :
                       pricingModel === 'PER_MONTH' ? 'Monthly Rate' :
                       pricingModel === 'PER_VISIT' ? 'Price per Visit' : 'Price'}
                    </Label>
                    <Input
                      id="price"
                      data-testid="service-price-input"
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="e.g., 50.00"
                      value={basePriceCents}
                      onChange={(e) => setBasePriceCents(e.target.value)}
                    />
                  </div>
                )}

                {pricingModel !== 'PER_MONTH' && (
                  <div className="space-y-2">
                    <Label htmlFor="duration">Duration (minutes) <span className="text-xs text-muted-foreground">— optional, for scheduling</span></Label>
                    <Input
                      id="duration"
                      data-testid="service-duration-input"
                      type="number"
                      min="1"
                      placeholder="e.g., 45"
                      value={durationMinutes}
                      onChange={(e) => setDurationMinutes(e.target.value)}
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="maxGroupSize">Max Group Size <span className="text-xs text-muted-foreground">— optional, for group services like trail rides or group lessons</span></Label>
                  <Input
                    id="maxGroupSize"
                    data-testid="service-max-group-input"
                    type="number"
                    min="1"
                    max="500"
                    placeholder="e.g., 6 riders"
                    value={maxGroupSize}
                    onChange={(e) => setMaxGroupSize(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    data-testid="service-description-input"
                    placeholder="Brief description of the service"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={3}
                  />
                </div>

                {/* Deposit Settings */}
                <div className="border-t pt-4 mt-4">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <Label htmlFor="depositEnabled" className="text-base font-semibold">Require Deposit</Label>
                      <p className="text-xs text-muted-foreground mt-1">
                        Clients will see deposit requirement before booking
                      </p>
                    </div>
                    <Switch
                      id="depositEnabled"
                      checked={depositEnabled}
                      onCheckedChange={setDepositEnabled}
                      data-testid="deposit-enabled-switch"
                    />
                  </div>

                  {depositEnabled && (
                    <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
                      <div className="space-y-2">
                        <Label htmlFor="depositType">Deposit Type *</Label>
                        <Select value={depositType} onValueChange={setDepositType}>
                          <SelectTrigger data-testid="deposit-type-select">
                            <SelectValue placeholder="Select deposit type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="FIXED">Fixed Amount</SelectItem>
                            <SelectItem value="PERCENTAGE">Percentage of Price</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {depositType === 'FIXED' && (
                        <div className="space-y-2">
                          <Label htmlFor="depositFixed">Fixed Deposit Amount ($) *</Label>
                          <Input
                            id="depositFixed"
                            data-testid="deposit-fixed-input"
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="e.g., 20.00"
                            value={depositFixedAmount}
                            onChange={(e) => setDepositFixedAmount(e.target.value)}
                          />
                        </div>
                      )}

                      {depositType === 'PERCENTAGE' && (
                        <div className="space-y-2">
                          <Label htmlFor="depositPercentage">Deposit Percentage (%) *</Label>
                          <Input
                            id="depositPercentage"
                            data-testid="deposit-percentage-input"
                            type="number"
                            min="1"
                            max="100"
                            placeholder="e.g., 20"
                            value={depositPercentage}
                            onChange={(e) => setDepositPercentage(e.target.value)}
                          />
                          {basePriceCents && depositPercentage && (
                            <p className="text-xs text-muted-foreground">
                              Preview: ${((parseFloat(basePriceCents) * parseInt(depositPercentage)) / 100).toFixed(2)} deposit
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex gap-2 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={() => setDialogOpen(false)}
                    disabled={submitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1"
                    disabled={submitting}
                    data-testid="save-service-button"
                  >
                    {submitting ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      editingService ? 'Update' : 'Create'
                    )}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {services.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground mb-4">No services yet</p>
              <Button onClick={() => handleOpenDialog()}>
                <Plus className="mr-2 h-4 w-4" />
                Add Your First Service
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2" data-testid="services-list">
            {services.map((service) => (
              <Card key={service.id} data-testid={`service-card-${service.id}`}>
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <CardTitle className="text-xl">{service.name}</CardTitle>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleOpenDialog(service)}
                        data-testid={`edit-service-${service.id}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(service.id)}
                        data-testid={`delete-service-${service.id}`}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex gap-4 text-sm flex-wrap">
                    {service.durationMinutes && (
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <Clock className="h-4 w-4" />
                        <span>{service.durationMinutes} min</span>
                      </div>
                    )}
                    <div className="flex items-center gap-1 text-muted-foreground">
                      <DollarSign className="h-4 w-4" />
                      <span>{formatPrice(service.basePriceCents, service.pricingModel)}</span>
                    </div>
                    {service.maxGroupSize && (
                      <div className="flex items-center gap-1 text-muted-foreground" data-testid={`service-group-badge-${service.id}`}>
                        <span className="inline-flex items-center px-2 py-0.5 bg-emerald-100 text-emerald-800 text-xs font-medium rounded">
                          Up to {service.maxGroupSize} riders
                        </span>
                      </div>
                    )}
                  </div>
                  {service.description && (
                    <p className="text-sm text-muted-foreground">{service.description}</p>
                  )}
                  {/* Deposit Badge */}
                  {service.depositEnabled && (
                    <div className="flex items-center gap-2 pt-2">
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-800 text-xs font-medium rounded">
                        {service.depositType === 'FIXED' ? (
                          <>
                            <DollarSign className="h-3 w-3" />
                            ${(service.depositFixedAmountCents / 100).toFixed(2)} deposit
                          </>
                        ) : (
                          <>
                            <Percent className="h-3 w-3" />
                            {service.depositPercentage}% deposit
                          </>
                        )}
                      </span>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
